import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FolderClock,
  Handshake,
  BadgePercent,
  Bookmark,
  Truck,
  FileText,
  Sparkles,
  Car,
  ArrowRight,
  Heart,
  BarChart3,
} from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../lib/api";
import { formatEUR } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../lib/favorites";
import KPICard from "../components/KPICard";
import VehicleCard from "../components/VehicleCard";

/**
 * "Strumenti" (Tools) — secondary hub that hosts the previously-on-Home
 * analytics widgets: KPI dashboard, IT↔DE market trend, top deals carousel,
 * watchlist and new arrivals. Nothing here is a new feature — this page
 * is a re-home of the widgets removed from Home to keep the dealer-facing
 * landing dead-simple. Reachable from Profilo → "Strumenti avanzati".
 */
export default function StrumentiPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { count: favCount } = useFavorites();
  const [stats, setStats] = useState(null);
  const [topDeals, setTopDeals] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistMargin, setWatchlistMargin] = useState(null);

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data));
    api
      .get("/vehicles", { params: { sort: "ai_score", limit: 6, status: "available" } })
      .then((r) => setTopDeals(r.data.items || []));
    api
      .get("/vehicles", { params: { sort: "newest", limit: 4 } })
      .then((r) => setNewArrivals(r.data.items || []));
  }, []);

  useEffect(() => {
    api
      .get("/favorites")
      .then((r) => {
        setWatchlist(r.data?.items || []);
        setWatchlistMargin(r.data?.total_margin_eur ?? 0);
      })
      .catch(() => {
        setWatchlist([]);
        setWatchlistMargin(0);
      });
    if (favCount != null) {
      api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [favCount]);

  return (
    <div className="py-6 md:py-10 space-y-10" data-testid="strumenti-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {t("strumenti.overline")}
        </p>
        <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
          {t("strumenti.title")}
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">{t("strumenti.subtitle")}</p>
      </div>

      {/* AI Copilot entry point */}
      <Link
        to="/ai"
        data-testid="strumenti-ai-cta"
        className="block rounded-2xl bg-[#0A0A0B] text-white p-6 md:p-8 hover:bg-[#111114] transition-colors"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-300 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> {t("strumenti.ai_overline")}
            </div>
            <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl tracking-tight">
              {t("strumenti.ai_title")}
            </h2>
            <p className="mt-2 text-sm text-slate-300 max-w-xl">{t("strumenti.ai_body")}</p>
          </div>
          <ArrowRight className="w-5 h-5 text-blue-300 shrink-0" />
        </div>
      </Link>

      {/* KPI grid */}
      <section>
        <h2 className="font-heading font-semibold text-xl md:text-2xl tracking-tight text-slate-950 mb-4 inline-flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" /> {t("strumenti.kpi_title")}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <KPICard testid="kpi-active-requests" title={t("home.kpi.active_requests")} value={stats ? stats.active_requests : "—"} icon={FolderClock} accent="info" delay={0.02} />
          <KPICard testid="kpi-in-negotiation" title={t("home.kpi.in_negotiation")} value={stats ? stats.in_negotiation : "—"} icon={Handshake} delay={0.05} />
          <KPICard testid="kpi-offers-received" title={t("home.kpi.offers_received")} value={stats ? stats.offers_received : "—"} icon={BadgePercent} accent="success" delay={0.08} />
          <KPICard testid="kpi-reserved-vehicles" title={t("home.kpi.reserved_vehicles")} value={stats ? stats.reserved_vehicles : "—"} icon={Bookmark} delay={0.11} />
          <KPICard testid="kpi-transports" title={t("home.kpi.transports_in_progress")} value={stats ? stats.transports_in_progress : "—"} icon={Truck} accent="info" delay={0.14} />
          <KPICard testid="kpi-docs" title={t("home.kpi.docs_to_complete")} value={stats ? stats.docs_to_complete : "—"} icon={FileText} accent="danger" delay={0.17} />
          <KPICard testid="kpi-watchlist-margin" title={t("home.kpi.watchlist_margin")} value={watchlistMargin != null ? formatEUR(watchlistMargin) : "—"} icon={Heart} accent="success" delay={0.2} />
          <KPICard testid="kpi-new-opportunities" title={t("home.kpi.new_opportunities")} value={stats ? stats.new_compatible_opportunities : "—"} icon={Sparkles} accent="info" delay={0.23} />
        </div>
      </section>

      {/* Seller KPI strip */}
      {stats?.has_dealer && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="home-seller-kpis"
          className="rounded-2xl bg-white border border-slate-100 p-5 md:p-6 shadow-sm"
        >
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                {t("home.seller.overline")}
              </p>
              <h2 className="mt-0.5 font-heading font-semibold text-lg tracking-tight text-slate-950">
                {t("home.seller.title")}
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/le-mie-auto" data-testid="home-seller-manage-stock" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold px-3 py-1.5 transition-colors">
                <Car className="w-3.5 h-3.5" /> {t("home.seller.manage_stock_cta")}
              </Link>
              <Link to="/pratiche" data-testid="home-seller-view-sales" className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 transition-colors">
                {t("home.seller.view_sales_cta")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KPICard testid="kpi-published-cars" title={t("home.seller.published_cars")} value={stats.published_cars} icon={Car} accent="info" delay={0.02} />
            <KPICard testid="kpi-pending-seller" title={t("home.seller.pending_seller_requests")} value={stats.pending_seller_requests} icon={FolderClock} delay={0.05} />
            <KPICard testid="kpi-active-sales" title={t("home.seller.active_seller_negotiations")} value={stats.active_seller_negotiations} icon={Handshake} delay={0.08} />
            <KPICard testid="kpi-sales-revenue" title={t("home.seller.sales_revenue_month_eur")} value={formatEUR(stats.sales_revenue_month_eur || 0)} icon={BadgePercent} accent="success" delay={0.11} />
          </div>
        </motion.div>
      )}

      {/* Trend chart */}
      {stats?.trend_it_de && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }} data-testid="trend-chart" className="rounded-2xl bg-white border border-slate-100 p-5 md:p-6 shadow-sm">
          <div className="flex items-end justify-between gap-3 mb-4">
            <div>
              <h2 className="font-heading font-semibold text-lg tracking-tight text-slate-950">
                {t("home.trend_title")}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">{t("home.trend_hint")}</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Index</div>
              <div className="font-heading font-bold text-xl tabular-nums text-emerald-600">
                {stats.trend_it_de[stats.trend_it_de.length - 1]?.value?.toFixed(1)}
              </div>
            </div>
          </div>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.trend_it_de} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis hide domain={["dataMin - 3", "dataMax + 3"]} />
                <Tooltip cursor={{ stroke: "#E2E8F0" }} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12, padding: "6px 10px" }} formatter={(v) => [v.toFixed(2), "Index"]} labelFormatter={(l) => new Date(l).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} />
                <Area type="monotone" dataKey="value" stroke="#10B981" strokeWidth={2} fill="url(#trendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Top deals */}
      <section data-testid="top-deals-section">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading font-semibold text-xl md:text-2xl tracking-tight text-slate-950">
            {t("home.top_deals")}
          </h2>
          <Link to="/marketplace?sort=ai_score" data-testid="top-deals-view-all" className="text-xs font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
            {t("home.view_all")} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="-mx-4 sm:mx-0 overflow-x-auto pb-2 hide-scrollbar">
          <div className="flex gap-4 px-4 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {topDeals.map((v) => (
              <div key={v.id} className="min-w-[280px] sm:min-w-0">
                <VehicleCard vehicle={v} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Watchlist */}
      <section data-testid="watchlist-section">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading font-semibold text-xl md:text-2xl tracking-tight text-slate-950 inline-flex items-center gap-2">
            <Heart className="w-5 h-5 text-rose-500" />
            {t("home.watchlist_title")}
          </h2>
        </div>
        {watchlist.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-100 p-8 text-center text-sm text-slate-500" data-testid="watchlist-empty">
            {t("home.watchlist_empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="watchlist-grid">
            {watchlist.slice(0, 4).map((v) => (
              <VehicleCard key={v.id} vehicle={v} />
            ))}
          </div>
        )}
      </section>

      {/* New arrivals */}
      <section data-testid="new-arrivals-section">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-heading font-semibold text-xl md:text-2xl tracking-tight text-slate-950 inline-flex items-center gap-2">
            <Car className="w-5 h-5 text-blue-600" />
            {t("home.new_arrivals")}
          </h2>
          <Link to="/marketplace?sort=newest" data-testid="new-arrivals-view-all" className="text-xs font-semibold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
            {t("home.view_all")} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {newArrivals.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      </section>
    </div>
  );
}
